import { useTranslation } from 'react-i18next';
import { useEffectOnce } from 'react-use';
import {
  BatchedInvoiceInput,
  BatchUploadType,
  useAttachInvoicesToBatchUploadMutation,
  useCreateBatchMutation,
} from '../../../../api/gql';
import { FullPageLoader } from '../../../../components';
import { useXmlDataStore } from '../../../../hooks/useXmlDataStore';
import { useXmlProcessController } from '../../../../hooks/useXmlProcessController';
import { notify } from '../../../../utils/notification';
import { useSafeNavigation } from '../../../../hooks/useSafeNavigation';

const TRANS_NS = 'xml-upload';

export const XmlCreateBatch = () => {
  const { t } = useTranslation(TRANS_NS);
  const { safeNavigate } = useSafeNavigation();

  const { xmlDataModel } = useXmlDataStore();
  const { updateXmlProcess } = useXmlProcessController();

  const { mutate } = useCreateBatchMutation({
    onSuccess: ({ createBatchUpload: { id } }) => {
      xmlDataModel.batchId = id;
      attachInvoicesToBatchUpload(id);
    },
    onError: () => {
      notify.negative({ message: t('createBatch.error') });
      //back to summary page - batch could not be created
      safeNavigate('/upload/xml/summary');
    },
  });

  const { mutate: attachInvoicesToBatch } = useAttachInvoicesToBatchUploadMutation({
    onSuccess: () => {
      if (!xmlDataModel.hasInvoicesForBilling) {
        // there is no invoices to pay
        safeNavigate('/');
        notify.neutral({ message: t('createBatch.nothingToPay') });
        return;
      }
      updateXmlProcess();
    },
    onError: () => {
      notify.negative({ message: t('createBatch.error') });
      //back to summary page - batch could not be created
      safeNavigate('/upload/xml/summary');
    },
  });

  const attachInvoicesToBatchUpload = (batchUploadId: string) => {
    const payload: BatchedInvoiceInput[] = xmlDataModel.xmlInvoicesForUpload.map((invoice) => {
      const customerId = xmlDataModel.xmlCustomers.find((c) => c.invoices.includes(invoice.id))?.details.id;
      const vendorId = xmlDataModel.xmlVendors.find((v) => v.invoices.includes(invoice.id))?.details.id;
      return {
        dueDate: invoice.details.dueDate ?? '',
        number: invoice.details.number ?? '',
        issueDate: invoice.details.issueDate ?? '',
        variableSymbol: invoice.details.variableSymbol?.toString() ?? '',
        bankAccount: invoice.details.bankAccount?.toString() ?? '',
        bankNumber: invoice.details.bankNumber?.toString() ?? '',
        currency: invoice.details.currency ?? '',
        amount: invoice.details.amount ?? 0,
        amountDue: invoice.details.amountDue ?? 0,
        customerId: customerId ?? '',
        vendorId: vendorId ?? '',
      };
    });

    attachInvoicesToBatch({
      input: {
        invoices: payload,
        batchUploadId: batchUploadId,
        scenarioId: xmlDataModel.scenarioId,
      },
    });
  };

  useEffectOnce(() => {
    mutate({
      input: {
        type: BatchUploadType.Export,
      },
    });
  });

  return <FullPageLoader />;
};
